/** The ADO Sync tab's queries: which Azure DevOps epics mirror into the portfolio. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface SyncConfig {
  id: string
  remoteProjectId: string
  remoteProjectName: string
  remoteEpicId: string
  remoteEpicTitle: string
  enabled: boolean
  team: string
  categoryId: string | null
  projectId: string | null
  lastSyncedAt: string | null
  lastSyncError: string | null
  lastItemCount: number | null
  hoursSinceSync: number | null
  isStale: boolean
  rowVersion: number
}

export interface DiscoveredEpic {
  remoteEpicId: string
  title: string
  state: string
  url: string
  syncId: string | null
  enabled: boolean
}

export interface DiscoveredProject {
  remoteProjectId: string
  name: string
  description: string
  url: string
  epics: DiscoveredEpic[]
}

export interface SyncRunResult {
  succeeded: boolean
  itemsSynced: number | null
  error: string | null
  durationMs: number | null
  startedAt: string
}

function invalidate(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['admin', 'ado'] })
  // A sync creates and updates projects, sprints and items across the app.
  void client.invalidateQueries({ queryKey: ['projects'] })
  void client.invalidateQueries({ queryKey: ['work'] })
}

export function useAdoOverview() {
  return useQuery({
    queryKey: ['admin', 'ado', 'overview'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/work-tracking', {})
      return data as unknown as {
        configured: boolean
        organizationUrl: string
        syncs: SyncConfig[]
      }
    },
  })
}

export function useAdoDiscovery(configured: boolean) {
  return useQuery({
    queryKey: ['admin', 'ado', 'discovery'],
    // Reads Azure DevOps live — one call per remote project — so only when the
    // provider is actually configured, and cached for the visit rather than refetched
    // on every focus.
    enabled: configured,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/work-tracking/discovery', {})
      return data as unknown as { projects: DiscoveredProject[] }
    },
  })
}

export function useEnableSync() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      remoteProjectId: string
      remoteProjectName: string
      remoteEpicId: string
      remoteEpicTitle: string
      team: string
      categoryId: string | null
    }) => {
      const { data } = await api.POST('/api/v1/admin/work-tracking/syncs', {
        body: {
          remote_project_id: input.remoteProjectId,
          remote_project_name: input.remoteProjectName,
          remote_epic_id: input.remoteEpicId,
          remote_epic_title: input.remoteEpicTitle,
          team: input.team,
          category_id: input.categoryId,
        } as never,
      })
      return data as unknown as SyncConfig
    },
    onSettled: () => invalidate(client),
  })
}

export function useUpdateSync() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      enabled?: boolean
      team?: string
      categoryId?: string | null
      clearCategory?: boolean
    }) => {
      const { data } = await api.PATCH('/api/v1/admin/work-tracking/syncs/{sync_id}', {
        params: { path: { sync_id: input.id } },
        body: {
          enabled: input.enabled,
          team: input.team,
          category_id: input.categoryId ?? undefined,
          clear_category: input.clearCategory ?? false,
        } as never,
      })
      return data as unknown as SyncConfig
    },
    onSettled: () => invalidate(client),
  })
}

export function useRunSync() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (syncId: string) => {
      const { data } = await api.POST('/api/v1/admin/work-tracking/syncs/{sync_id}/run', {
        params: { path: { sync_id: syncId } },
      })
      return data as unknown as SyncRunResult
    },
    onSettled: () => invalidate(client),
  })
}

export function useRunAllSyncs() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/admin/work-tracking/run', {})
      return data as unknown as { runs: SyncRunResult[]; succeeded: number; failed: number }
    },
    onSettled: () => invalidate(client),
  })
}
